<template>
  <div class="custom-container mt-4">
    <!-- Dropdown -->
    <div class="d-flex mb-3 p-3 gap-3 flex-wrap align-items-center justify-content-start">
      <div
          class="px-4 py-2 rounded-pill shadow-sm d-flex align-items-center mr-2"
          style="background:#bbdefb; color:#0d47a1; font-weight:600; height: 45px;"
      >
        <span class="me-2">🎯</span> Select a Candidate Type
      </div>
      <select
          v-model="selectedCandidateType"
          class="form-select rounded-pill shadow-sm px-4 py-2 border-0 custom-select-arrow"
          style="height: 45px; min-width: 300px; padding-right: 3rem;"
      >
        <option value="">Select Candidate Type</option>
        <option v-for="name in availableTypes" :key="name" :value="name">
          {{ name }}
        </option>
      </select>
    </div>

    <!-- Info Phase -->
    <div v-if="!isInfoOpen" class="text-center text-muted py-5">
      Candidate information isn’t open yet.
    </div>
    <div v-else-if="!selectedCandidateType" class="text-center text-muted py-5">
      Please select a candidate type to view.
    </div>

    <!-- Cards -->
    <div v-else class="grid-container">
      <div v-for="(c, idx) in filteredCandidates" :key="c._id" class="grid-card">
        <CCard
            @click="hasVoted ? showAlreadyVoted() : selectCandidate(c)"
            class="shadow d-flex flex-column align-items-center text-center mx-2 position-relative"
            :class="{
            'selected-card border-success': isSelected(c._id),
            'not-allowed': hasVoted
          }"
            :style="{
            borderRadius: '20px',
            width: '100%',
            cursor: hasVoted ? 'not-allowed' : 'pointer'
          }"
        >
          <!-- Overlay if already voted -->
          <div
              v-if="hasVoted"
              class="vote-overlay"
              style="
              position: absolute;
              top: 0; left: 0; right: 0; bottom: 0;
              background: rgba(255, 255, 255, 0.85);
              display: flex;
              justify-content: center;
              align-items: center;
              font-weight: bold;
              font-size: 1rem;
              color: #333;
              z-index: 10;
              border-radius: 20px;
            "
          >
            ✅ You have already voted
          </div>

          <div class="card-container mt-3" style="position: relative;">
            <div class="image-wrapper">
              <img :src="fullImageUrl(c.image)" class="candidate-image" />
            </div>
            <div class="card-number-bottom">
              {{ c.originalIndex + 1 }}
            </div>
          </div>

          <div v-if="isSelected(c._id)" class="selected-check">✅</div>
          <p class="fw-bold mb-2 mt-1" style="font-size:1rem;">
            {{ c.name }}<br />
            ({{ c.studentId }})
          </p>
          <p class="mb-1" style="font-size:0.75rem;">{{ c.department }}</p>
          <p class="mb-2" style="font-size:0.75rem;">{{ c.major }}</p>

          <div v-if="isShowResult" class="mb-2 text-center">
            <div style="font-size:1rem;color:#666;">Score</div>
            <div style="font-size:1.8rem;font-weight:bold;color:#000;">
              {{ c.votes || 0 }}
            </div>
          </div>

          <CButton
              color="primary"
              variant="outline"
              size="sm"
              shape="pill"
              class="d-inline-flex align-items-center gap-2 mb-4"
              @click.stop="showPolicy(c)"
          >
            <CIcon name="cil-magnifying-glass" size="sm" />
            <span>View Policy</span>
          </CButton>
        </CCard>
      </div>
    </div>

    <!-- Policy Modal -->
    <div v-if="policyModalVisible" class="custom-modal-backdrop" @click.self="policyModalVisible = false">
      <div class="custom-modal-box">
        <h4 class="mb-3 text-center">Policy of {{ currentCandidate.name }}</h4>
        <div
            v-html="currentCandidate.policy"
            class="policy-html mb-4"
            style="max-height: 400px; overflow-y: auto; padding-right: 10px;"
        />
        <CButton color="danger" @click="policyModalVisible = false">Close</CButton>
      </div>
    </div>

    <!-- Footer Vote Component -->
    <Vote
        v-if="isVotingOpen"
        :isVotingOpen="isVotingOpen"
        :selectedCandidate="selectedCandidate"
        :selectedCandidateName="getSelectedCandidateName"
        :voteType="selectedCandidateType"
        :typeId="(types.find(t => t.type_name === selectedCandidateType) || {})._id"
        :canVote="canVote"
        :hasVoted="hasVoted"
    />
  </div>
</template>

<script>
import { CRow, CCol, CCard, CButton } from '@coreui/vue'
import Vote from '@/projects/views/vote_Info/index.vue'
import api from '@/service/api.js'
import moment from 'moment-timezone'
import '@/assets/css/info.css'

const REGISTER_EVENT_ID = '68624a7c09800f23242c3da5'
const INFO_EVENT_ID = '686245fbb5e0dc05f8d093ff'
const VOTE_EVENT_ID = '68624605b5e0dc05f8d09402'
const RESULT_EVENT_ID = '68624611b5e0dc05f8d09405'

export default {
  name: 'InfoSchool',
  components: { CRow, CCol, CCard, CButton, Vote },
  data() {
    return {
      types: [],
      events: [],
      candidates: [],
      setters: [],
      voteCounts: [],
      selectedCandidateType: '',
      selectedCandidate: [],
      policyModalVisible: false,
      currentCandidate: null,
      imageBaseURL: 'https://api-election-su.mfu.ac.th',
      voteStatus: null,
      hasVoted: false,
      userDepartmentId: null
    }
  },
  watch: {
    selectedCandidateType: {
      immediate: true,
      async handler(newVal) {
        if (!newVal) return
        const user = JSON.parse(localStorage.getItem('user') || '{}')
        const typeObj = this.types.find(t => t.type_name === newVal)
        if (!typeObj) return

        try {
          const res = await api.vote('check', {
            people: user._id,
            type: typeObj._id,
            year: new Date().getFullYear()
          })
          this.voteStatus = res?.data?.status || null
          this.hasVoted = this.voteStatus === 'voted' || this.voteStatus === 'abstained'
        } catch (err) {
          console.warn('Vote status check failed', err)
          this.hasVoted = false
        }
      }
    }
  },
  async created() {
    const user = JSON.parse(localStorage.getItem('user') || '{}')
    this.userDepartmentId = user?.department?._id || user?.department || null

    this.types = (await api.type('get')).data.data || []
    this.events = (await api.event('get')).data.data || []

    const voteRes = await api.vote('getVoteCounts')
    this.voteCounts = voteRes.data.data || []

    const rawC = (await api.candidate('getCandidates')).data.data || []
    this.candidates = rawC.map((c, index) => {
      const acc = c.accountID || {}
      const dept = acc.department
      let deptId = null
      let deptName = '-'

      // Check if populated object
      if (typeof dept === 'object' && dept !== null) {
        deptId = dept._id || null
        deptName = dept.department_name || '-'
      }

      // Fallback: dept is just the ObjectId string
      if (typeof dept === 'string') {
        deptId = dept
        deptName = '-'
      }

      return {
        _id: c._id,
        name: acc.name || '-',
        studentId: acc.studentId || '-',
        department: deptName,
        departmentId: deptId,
        major: acc.major?.major_name || '-',
        image: c.picture || c.image,
        policy: c.policy,
        typeId: c.type?._id || c.type,
        typeName: c.type?.type_name || '',
        eventId: c.event?._id || c.event,
        eventName: c.event?.event_name || '',
        status: c.status?.status_name || '-',
        originalIndex: index
      }
    })
    const rawS = (await api.setter('get')).data.data || []
    this.setters = rawS.map(s => ({
      eventId: s.title?._id || s.title,
      typeId: s.type?._id || s.type,
      start: moment.utc(s.startDate).tz('Asia/Bangkok'),
      end: moment.utc(s.endDate).tz('Asia/Bangkok')
    }))
  },
  methods: {
    fullImageUrl(path) {
      return path ? `${this.imageBaseURL}${path}` : ''
    },
    selectCandidate(c) {
      if (!this.isVotingOpen || this.hasVoted) {
        this.$toast?.error('You have already voted or abstained.')
        return
      }
      const isMulti = this.selectedCandidateType === 'Student council member (Normal)'
      const idx = this.selectedCandidate.indexOf(c._id)
      if (isMulti) {
        if (idx === -1) this.selectedCandidate.push(c._id)
        else this.selectedCandidate.splice(idx, 1)
      } else {
        this.selectedCandidate = [c._id]
      }
    },
    isSelected(id) {
      return this.selectedCandidate.includes(id)
    },
    showAlreadyVoted() {
      this.$toast?.warning('You have already voted or abstained for this position.')
    },
    showPolicy(c) {
      this.currentCandidate = c
      this.policyModalVisible = true
    }
  },
  computed: {
    isInfoOpen() {
      const now = moment().tz('Asia/Bangkok')
      return this.setters.some(w =>
          w.eventId === INFO_EVENT_ID &&
          now.isBetween(w.start, w.end, null, '[]')
      )
    },
    availableTypes() {
      return this.setters
          .filter(w => w.eventId === INFO_EVENT_ID)
          .map(w => this.types.find(t => t._id === w.typeId)?.type_name)
          .filter((v, i, a) => v && a.indexOf(v) === i)
    },
    filteredCandidates() {
      if (!this.isInfoOpen || !this.selectedCandidateType) return [];

      const userDeptId = this.userDepartmentId;
      return this.candidates
          .filter(c => {
            const matchesType = c.typeName === this.selectedCandidateType;
            const matchesEvent = c.eventId === REGISTER_EVENT_ID;
            const isApproved = c.status === 'Approved';
            const isSameDept = this.selectedCandidateType !== 'Student council member (School of)' ||
                (this.userDepartmentId && String(c.departmentId?._id || c.departmentId) === String(this.userDepartmentId));
            return matchesType && matchesEvent && isApproved && isSameDept;
          })
          .map(c => {
            const voteBlock = this.voteCounts.find(v => v.type === c.typeName);
            const candidate = voteBlock?.candidates.find(x => x._id === c._id);
            return { ...c, votes: candidate?.votes || 0 };
          })
          .sort((a, b) => b.votes - a.votes);
    },
    isVotingOpen() {
      const now = moment().tz('Asia/Bangkok')
      const w = this.setters.find(w =>
          w.eventId === VOTE_EVENT_ID &&
          w.typeId === this.types.find(t => t.type_name === this.selectedCandidateType)?._id
      )
      return !!w && now.isBetween(w.start, w.end, null, '[]')
    },
    canVote() {
      return this.selectedCandidate.length > 0
    },
    isShowResult() {
      const now = moment().tz('Asia/Bangkok')
      const w = this.setters.find(w =>
          w.eventId === RESULT_EVENT_ID &&
          w.typeId === this.types.find(t => t.type_name === this.selectedCandidateType)?._id
      )
      return !!w && now.isBetween(w.start, w.end, null, '[]')
    }
  }
}
</script>

<style scoped>
.selected-card {
  border: 2px solid #28a745 !important;
}
.not-allowed {
  opacity: 0.6;
  cursor: not-allowed;
}
</style>
